import React, { useEffect, useMemo, useState } from "react"
import { View, StyleProp, ViewStyle, Pressable, KeyboardAvoidingView, Platform, TextInput, ScrollView } from "react-native"
import {
	AdminViewDocument,
	AdminViewQuery,
	useInterruptRentingMutation,
	useLockersystemUsersQuery,
	useSendCustomRentingMessageMutation,
	useSendRentingLateReminderMutation,
	useTransferRentingMutation,
	useTransferUserSearchQuery,
} from "./queries.generated"
import { toString } from "lodash-es"
import MyText from "../../components/MyText"
import MainScrollPanel from "../../components/layout/MainScrollPanel"
import Base from "../../components/layout/Base"
import { useDurationToDeadline } from "../../utils/Duration"
import { useTranslation } from "react-i18next"
import { ActiveViewerDocument } from "../ActiveItems/queries.generated"
import { Button, ErrorMessage, Modal, Text, TouchButton, TouchButtonRow } from "pusatec-react-native"
import { RentingState } from "../../doorhub-types"
import { LocalDatetime } from "../../components/local-datetime"
import { AiOutlineCheck, AiOutlineClose} from "react-icons/ai"
import { AdminToolbar } from "./admin_toolbar"
import { TinderCard } from "../../components/TinderCard"
import { HeaderBar } from "../../components/Headerbar"
import { useCancelRentingMutation } from "../RentItem/queries.generated"
import { useTransientMessage } from "../../utils/use_transient_message"
import { SearchBar } from "react-native-elements"
import { theme } from "../../theme"
import { useDebouncedState } from "pusatec-react-utility"

type Renting = NonNullable<AdminViewQuery["lockersystem"]>["activeRentings"]["rentings"][0]

export function ManageRentings(props: {
    systemId: string
    rentings: Renting[]
    onRefetch: () => void
    loading: boolean
    style?: StyleProp<ViewStyle>,
    interruptRenting: any
    updateRenting: any
    givePermit: any
    declinePermit: any
}) {
	const { systemId, rentings, loading, onRefetch, style } = props
	const { t } = useTranslation()
	const [sendCustomEmail, { loading: sendingCustomMessage }] = useSendCustomRentingMessageMutation()
	const [sendRentingLateReminder, { loading: sendingReminder, error: reminderError }] = useSendRentingLateReminderMutation()
	const [messageModalVisible, setMessageModalVisible] = useState(false)
	const [messageSubject, setMessageSubject] = useState("")
	const [messageBody, setMessageBody] = useState("")
	const [messageError, setMessageError] = useState<string | null>(null)
	const [messageTarget, setMessageTarget] = useState<"single" | "group">("single")
	const [transferModalVisible, setTransferModalVisible] = useState(false)
	const [transferSearch, debouncedTransferSearch, setTransferSearch] = useDebouncedState("", 300)
	const [transferTargetUserId, setTransferTargetUserId] = useState<string | null>(null)
	const [cancelRenting] = useCancelRentingMutation()
	const [transferRenting, transferRentingM] = useTransferRentingMutation()
	const STATUS_FEEDBACK_DURATION = 4000
	const [messageStatus, setMessageStatus] = useTransientMessage(STATUS_FEEDBACK_DURATION)
	const [reminderStatus, setReminderStatus] = useTransientMessage(STATUS_FEEDBACK_DURATION)
	const [transferStatus, setTransferStatus] = useTransientMessage(STATUS_FEEDBACK_DURATION)
	const rentingGroups = useMemo(() => {
		const groups: { [key: string]: Renting[] } = {}

		for (const renting of rentings) {
			if (renting.state === RentingState.Finished) {
				continue
			}

			let groupName

			if (renting.state === RentingState.NeedsPermit) {
				groupName = t("Permission required")
			} else if (renting.state === RentingState.WaitsPickup) {
				groupName = t("Waits pickup")
			} else if (renting.state === RentingState.WaitsReturn) {
				groupName = t("Waits return")
			} else if (renting.locker?.lockerGroup?.name) {
				groupName = renting.locker.lockerGroup.name
			} else {
				groupName = t("Unassigned group")
			}

			if (!groups[groupName]) {
				groups[groupName] = []
			}

			groups[groupName].push(renting)
		}

		return groups
	}, [rentings])

	const [selectedRentingId, setSelectedRentingId] = useState<string>("")

	const selectedRenting = useMemo(() => {
		return rentings.find(p => p.id === selectedRentingId)
	}, [rentings, selectedRentingId])

	const selectedRentingLate = useMemo(() => {
		if (!selectedRenting?.returnItemBefore) {
			return false
		}

		return new Date(selectedRenting.returnItemBefore) < new Date()
	}, [selectedRenting])

	const borrowerSearchTerm = (
		selectedRenting?.user?.gmail ??
		selectedRenting?.user?.userName ??
		selectedRenting?.user?.name ??
		""
	).trim()
	const shouldSearchBorrower = borrowerSearchTerm.length > 0
	const { data: borrowerUsersData } = useLockersystemUsersQuery({
		skip: !shouldSearchBorrower,
		variables: {
			query: {
				lockersystemId: systemId,
				search: shouldSearchBorrower ? borrowerSearchTerm : undefined,
			},
		},
	})
	const transferSearchTerm = debouncedTransferSearch.trim()
	const { data: transferUsersData, loading: transferUsersLoading, error: transferUsersError } = useTransferUserSearchQuery({
		skip: !transferModalVisible || debouncedTransferSearch.trim().length < 3,
		variables: {
			input: {
				accessToLockersystemId: systemId,
				searchWord: transferSearchTerm,
				hasEmail: true,
				showNotLoggedIn: true,
			}
		}
	})

	const borrowerPermission = borrowerUsersData?.lockersystemUserPermissions?.find(permission => {
		const user = permission.user
		if (!user) {
			return false
		}
		return (
			(user.email && user.email === selectedRenting?.user?.gmail) ||
			(user.userName && user.userName === selectedRenting?.user?.userName)
		)
	})
	const borrowerId = selectedRenting?.user?.id ?? borrowerPermission?.user?.id

	const [interruptRenting, interruptRentingM] = useInterruptRentingMutation()
	const selectedRentingUserEmails = [
		selectedRenting?.user?.gmail?.toLowerCase(),
		selectedRenting?.user?.userName?.toLowerCase(),
	].filter(Boolean)
	const transferUsers = Array.from(new Map((transferUsersData?.users.users ?? [])
		.filter(user => user.id !== selectedRenting?.user?.id)
		.filter(user => !selectedRentingUserEmails.includes(user.email?.toLowerCase()))
		.map(user => [user.email?.toLowerCase() || user.id, user] as const)).values())
		.slice(0, 10)
		.sort((first, second) => (first.name || first.email || "").localeCompare(second.name || second.email || ""))
	const selectedTransferUser = transferUsers.find(user => user?.id === transferTargetUserId)
	const transferSearchReady = transferSearch.trim().length >= 3

	const openMessageModal = (target: "single" | "group") => {
		setMessageSubject("")
		setMessageBody("")
		setMessageError(null)
		setMessageStatus(null)
		setMessageTarget(target)
		setMessageModalVisible(true)
	}

	const closeMessageModal = () => {
		setMessageModalVisible(false)
		setMessageTarget("single")
	}

	const openTransferModal = () => {
		setTransferSearch("")
		setTransferTargetUserId(null)
		setTransferStatus(null)
		setTransferModalVisible(true)
	}

	const closeTransferModal = () => {
		setTransferModalVisible(false)
		setTransferTargetUserId(null)
	}

	const handleTransferRenting = () => {
		if (!selectedRenting || !transferTargetUserId) {
			return
		}

		transferRenting({
			variables: {
				input: {
					rentingId: selectedRenting.id,
					userId: transferTargetUserId
				}
			},
			refetchQueries: [
				{
					query: ActiveViewerDocument,
				},
				{
					query: AdminViewDocument,
					variables: { systemId }
				}
			]
		}).then(() => {
			closeTransferModal()
			setSelectedRentingId("")
			setTransferStatus(t("Renting transferred"))
		}).catch(() => undefined)
	}

	const handleSendCustomMessage = () => {
		const trimmedSubject = messageSubject.trim()
		const trimmedBody = messageBody.trim()

		if (!trimmedSubject || !trimmedBody) {
			setMessageError(t("Subject and message are required"))
			return
		}

		if (messageTarget === "single" && !borrowerId) {
			setMessageError(t("Borrower information missing"))
			return
		}

		setMessageError(null)

		const input: {
			subject: string
			text: string
			includeReplyTo: boolean
			userIds?: string[]
			lockersystemId?: string
			userRentingInProgress?: boolean
		} = {
			subject: trimmedSubject,
			text: trimmedBody,
			includeReplyTo: true
		}

		if (messageTarget === "single") {
			input.userIds = [borrowerId!]
		} else {
			input.lockersystemId = systemId
			input.userRentingInProgress = true
		}

		sendCustomEmail({
			variables: {
				input
			}
		}).then(() => {
			setMessageModalVisible(false)
			setMessageStatus(t("Message sent"))
		}).catch(error => {
			setMessageError(error.message || t("Failed to send message"))
		})
	}

	const handleSendReminder = () => {
		if (!selectedRenting) {
			return
		}

		setReminderStatus(null)

		sendRentingLateReminder({
			variables: {
				input: {
					rentingId: selectedRenting.id
				}
			}
		}).then(() => {
			setReminderStatus(t("Reminder sent"))
		}).catch(() => undefined)
	}

	return <View style={[{ flex: 1 }, style]} >
		<MainScrollPanel
			style={{ borderWidth: 0, backgroundColor: undefined }}
			onRefresh={onRefetch}
			refreshing={loading}
		>
			{Object.entries(rentingGroups).map(([key, rentings]) => (
				<View key={key} style={{ marginBottom: 10 }}>
					<HeaderBar text={key} />
					{rentings.map(renting =>
						<ManageRentingRow
							key={renting.id}
							renting={renting}
							selected={renting.id === selectedRentingId}
							onSelect={() =>
								setSelectedRentingId((current) =>
									current === renting.id ? "" : renting.id,
								)}
							givePermit={props.givePermit}
							declinePermit={props.declinePermit}
							updateRenting={props.updateRenting}

						/>
					)}
				</View>
			))}
		</MainScrollPanel>

		<AdminToolbar>
			<View style={{ flexGrow: 1, flexDirection: "row", flexWrap: "wrap" }}>

				{interruptRentingM.error && <ErrorMessage extra={interruptRentingM.error} />}
				{transferRentingM.error && <ErrorMessage extra={transferRentingM.error} />}
				{selectedRenting && selectedRenting.state !== RentingState.WaitsPickup && selectedRenting.state !== RentingState.NeedsPermit && <TouchButton
					text={t("Interrupt renting")}
					loading={interruptRentingM.loading}
					onPress={() => {
						interruptRenting({
							variables: {
								rentingId: selectedRenting.id
							},
							refetchQueries: [
								{
									query: ActiveViewerDocument,
								},
								{
									query: AdminViewDocument,
									variables: { systemId }
								}
							]
						}).then(_ => {
							setSelectedRentingId("")
						})
					}}
				/>}
				{selectedRenting && selectedRenting.state !== RentingState.WaitsPickup && selectedRenting.state !== RentingState.NeedsPermit && <TouchButton
					text={t("Transfer renting")}
					type="secondary"
					loading={transferRentingM.loading}
					disabled={transferRentingM.loading}
					onPress={openTransferModal}
					containerStyle={{ marginLeft: 5 }}
				/>}
				{selectedRenting && selectedRenting.state === RentingState.WaitsPickup && <TouchButton
					text={t("Cancel renting")}
					loading={interruptRentingM.loading}
					onPress={() => {
						cancelRenting({
							variables: {
								rentingId: selectedRenting.id
							},
							refetchQueries: [
								{
									query: ActiveViewerDocument,
								},
								{
									query: AdminViewDocument,
									variables: { systemId }
								}
							]
						}).then(_ => {
							setSelectedRentingId("")
						})
					}}
				/>}
			</View>
			{selectedRenting ? (
				<View style={{ flexDirection: "column", alignItems: "flex-end" }}>
					<View style={{ flexDirection: "row" }}>
						<TouchButton
							text={t("Send")}
							type="secondary"
							onPress={() => openMessageModal("single")}
							loading={sendingCustomMessage}
							disabled={sendingCustomMessage}
							containerStyle={{ marginRight: 5 }}
						/>
					</View>
					{messageStatus ? <MyText type="small" text={messageStatus} /> : null}
					{reminderStatus ? <MyText type="small" text={reminderStatus} /> : null}
					{transferStatus ? <MyText type="small" text={transferStatus} /> : null}
					{reminderError && <ErrorMessage extra={reminderError} />}
				</View>
			) : (
				<View style={{ flexDirection: "column", alignItems: "flex-end" }}>
					<View style={{ flexDirection: "row" }}>
						<TouchButton
							text={t("Group message")}
							type="secondary"
							onPress={() => openMessageModal("group")}
							loading={sendingCustomMessage}
							disabled={sendingCustomMessage}
							containerStyle={{ marginRight: 5 }}
						/>
					</View>
					{messageStatus ? <MyText type="small" text={messageStatus} /> : null}
					{transferStatus ? <MyText type="small" text={transferStatus} /> : null}
				</View>
			)}
		</AdminToolbar>
		{transferModalVisible && selectedRenting && (
			<Modal
				visible={transferModalVisible}
				onClose={closeTransferModal}
				onRequestClose={closeTransferModal}
				autoScroll={false}
				backgroundStyle={{ padding: 20 }}
				contentContainerStyle={{ borderRadius: 12, maxWidth: 760, width: "100%" }}
				contentStyle={{ padding: 0 }}
			>
				<View style={{
					backgroundColor: "white",
					padding: 20,
					borderRadius: 10,
					elevation: 4,
					maxHeight: "90%",
				}}>
					<MyText text={t("Transfer renting")} type="h2" />
					<MyText
						text={t("Choose target user for {{item}}", { item: selectedRenting.item?.name ?? "" })}
						style={{ marginTop: 10 }}
					/>
					<SearchBar
						lightTheme
						inputStyle={theme.Input.inputStyle}
						errorStyle={theme.Input.errorMessageStyle}
						labelStyle={theme.Input.labelStyle}
						containerStyle={theme.Input.containerStyle}
						inputContainerStyle={theme.Input.inputContainerStyle}
						placeholder={t("Search user")}
						value={transferSearch}
						onChangeText={text => {
							setTransferSearch(text)
							setTransferTargetUserId(null)
						}}
					/>
					<ScrollView style={{ height: 300, marginTop: 10 }}>
						{transferSearch.trim().length > 0 && !transferSearchReady ? (
							<MyText text={t("Type at least 3 characters")} style={{ textAlign: "center", marginTop: 10 }} />
						) : null}
						{transferUsersError ? <ErrorMessage extra={transferUsersError} /> : null}
						{transferSearchReady && transferUsersLoading ? (
							<MyText text={t("Loading")} style={{ textAlign: "center", marginTop: 10 }} />
						) : null}
						{transferSearchReady && !transferUsersLoading && transferUsers.length === 0 ? (
							<MyText text={t("No users found")} style={{ textAlign: "center", marginTop: 10 }} />
						) : null}
						{transferUsers.map(user => (
							<Base
								key={user!.id}
								padding={12}
								selected={user?.id === transferTargetUserId}
								onPress={() => setTransferTargetUserId(user?.id ?? null)}
							>
								<Text text={user?.name || user?.email || user?.userName || "-"} bold />
								{user?.email ? <Text text={user.email} /> : null}
								{user?.userName ? <Text text={user.userName} /> : null}
							</Base>
						))}
					</ScrollView>
					{selectedTransferUser ? (
						<MyText
							text={t("Transfer to {{user}}", { user: selectedTransferUser.name || selectedTransferUser.email || selectedTransferUser.userName })}
							style={{ marginTop: 10 }}
						/>
					) : null}
					{transferRentingM.error && <ErrorMessage extra={transferRentingM.error} />}
					<View style={{ flexDirection: "row", justifyContent: "flex-end", marginTop: 10 }}>
						<TouchButtonRow>
							<TouchButton text={t("Cancel")} type="secondary" onPress={closeTransferModal} />
							<TouchButton
								text={t("Transfer renting")}
								type="primary"
								onPress={handleTransferRenting}
								loading={transferRentingM.loading}
								disabled={!transferTargetUserId || transferRentingM.loading}
								containerStyle={{ marginLeft: 10 }}
							/>
						</TouchButtonRow>
					</View>
				</View>
			</Modal>
		)}
		{messageModalVisible && (
			<Modal
				visible={messageModalVisible}
				onClose={closeMessageModal}
				onRequestClose={closeMessageModal}
				autoScroll={false}
				backgroundStyle={{ padding: 20 }}
				contentContainerStyle={{ borderRadius: 12, maxWidth: 760, width: "100%" }}
				contentStyle={{ padding: 0 }}
			>
				<KeyboardAvoidingView
					behavior={Platform.OS === "ios" ? "padding" : "height"}
					style={{ width: "100%" }}
				>
					<View style={{
						backgroundColor: "white",
						padding: 20,
						borderRadius: 10,
						elevation: 4,
						maxHeight: "90%",
					}}>
						<MyText text={t("Send")} type="h2" />
						{selectedRenting ? (
							<View style={{ marginTop: 20 }}>
								<MyText text={t("Send renting late reminder")} type="h2" bold />
								<View style={{ marginTop: 12, alignItems: "flex-start" }}>
									<TouchButton
										text={t("Send reminder")}
										onPress={handleSendReminder}
										loading={sendingReminder}
										disabled={!selectedRentingLate || sendingReminder}
										type="primary"
										containerStyle={{ marginTop: 0 }}
									/>
									{reminderStatus ? <MyText type="small" text={reminderStatus} style={{ marginTop: 8 }} /> : null}
									{reminderError && <ErrorMessage extra={reminderError} style={{ marginTop: 8 }} />}
								</View>
							</View>
						) : null}
						<View style={{ marginTop: 24 }}>
							<MyText text={t("Send custom message")} type="h2" bold />
							<TextInput
								placeholder={t("Message subject")}
								value={messageSubject}
								onChangeText={setMessageSubject}
								style={{
									borderWidth: 1,
									borderColor: "#ccc",
									borderRadius: 6,
									padding: 10,
									marginTop: 10,
								}}
							/>
							<TextInput
								placeholder={t("Message body")}
								value={messageBody}
								onChangeText={setMessageBody}
								style={{
									borderWidth: 1,
									borderColor: "#ccc",
									borderRadius: 6,
									padding: 10,
									marginTop: 10,
									height: 140,
									textAlignVertical: "top",
								}}
								multiline
							/>
							{messageError && <MyText type="error" text={messageError} />}
							<View style={{
								flexDirection: "row",
								marginTop: 10,
								justifyContent: "flex-end"
							}}>
								<TouchButtonRow>
									<TouchButton text={t("Cancel")} type="secondary" onPress={closeMessageModal} />
									<TouchButton
										text={t("Send")}
										type="primary"
										onPress={handleSendCustomMessage}
										loading={sendingCustomMessage}
										disabled={sendingCustomMessage || !messageSubject.trim() || !messageBody.trim()}
										containerStyle={{ marginLeft: 10 }}
									/>
								</TouchButtonRow>
							</View>
						</View>
					</View>
				</KeyboardAvoidingView>
			</Modal>
		)}
	</View>
}

export function ManageRentingRow(props: {
    renting: Renting
    selected?: boolean
    givePermit: any
    onSelect?: () => void
    declinePermit: any
    updateRenting: any

}) {
	const { renting, selected, onSelect } = props
	const { t } = useTranslation()
	const [returnBefore, setReturnBefore] = useState<string | null>(null)

	const givePermitSwipe = () => {
		if(!renting.id) {
			return
		}
		props.givePermit(renting.id)
	}

	const declinePermitSwipe = () => {
		if(!renting.id) {
			return
		}
		props.declinePermit(renting.id)
	}
	const updateRenting = () => {
		if(!renting.id) {
			return
		}

		props.updateRenting({
			variables: {
				input: {
					rentingId: renting.id,
					returnItemBefore: returnBefore ? returnBefore : null
				}
			}
		})
	}

	useEffect(() => {
		if (!renting.returnItemBefore) {
			return
		}

		setReturnBefore(renting.returnItemBefore)
	}, [renting.returnItemBefore, setReturnBefore])

	const rentTimeLeft = useDurationToDeadline(
		renting.returnItemBefore ? new Date(renting.returnItemBefore) : undefined,
	)

	const overtimeState = rentTimeLeft.overtime && rentTimeLeft.duration ? (
		<MyText
			type="error"
			text={t("{{time}} overtime!", {
				time: rentTimeLeft.duration.format(t),
			})}
		/>
	) : null

	const rentingLate = renting?.returnItemBefore ?
		new Date(renting?.returnItemBefore) < new Date() :
		false

	const needsPermit = renting.state === RentingState.NeedsPermit
	const WaitsPickup = renting.state === RentingState.WaitsPickup
	return <TinderCard onSwipeLeft={declinePermitSwipe} onSwipeRight={givePermitSwipe} disabled={!needsPermit} seleted={selected}>
		<Base
			key={renting.id}
			onPress={onSelect}
			selected={selected}
			style={{
				borderRightColor: rentingLate ? "red" : "white",
				borderRightWidth: 8,
				backgroundColor: WaitsPickup ? "#CCFFCC" : "white",
			}}
		>
			<View style={{flexDirection:"column"}}  >

				<Text text={`${toString(renting.user?.name)}`} bold />
				<MyText text={`${toString(renting.user?.gmail)}`} bold />
				<View style={{ flexDirection: "row", flexWrap: "wrap" }}>

					<Text text={`${toString(renting.item?.name)}`} style={{ flexGrow: 1 }}  />

					{needsPermit &&

            <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
            	<Pressable onPress={() => {
            		if(!renting.id) {
            			return
            		}
            		props.declinePermit(renting.id)
            	}}>
            		<AiOutlineClose size={30} style={{marginRight:20,color: "#f54058"}} />
            	</Pressable>
            	<Pressable onPress={() => {
            		if(!renting.id) {
            			return
            		}
            		props.givePermit(renting.id)
            	}}>
            		<AiOutlineCheck size={30} style={{color:"#55c955"}} />
            	</Pressable>
            </View>}
				</View>
				{renting.locker &&
            <Text text={t("Locker {{name}}", { name: toString(renting.locker?.name) })} />}

				{selected ? <Pressable onPress={(e) => e.stopPropagation()}>
					{renting?.returnItemBefore
						? <Text text={t("Return before {{deadline}}")} />
						: null}

					{returnBefore ?
						<LocalDatetime
							style={{
								width: "200px",
								height: "25px"
							}}
							min={new Date()}
							datetime={returnBefore}
							onChange={d => setReturnBefore(d)}
						/>
						: null}

					{renting?.returnItemBefore != returnBefore &&
                <Button text="Update"
                	containerStyle={{ width: "100px" }}
                	onPress={() => {
                		updateRenting()
                	}} />}
					{}

					{overtimeState}
				</Pressable> : null}

			</View>
		</Base>
	</TinderCard>
}
